import { useMemo, useState } from 'react';
import { btnSolid, btnGhost, sectionDesc } from '../lib/ui.ts';
import {
  seedConfirmState,
  brandCriticalMissing,
  toMappedTokens,
  isColorValue,
  COLOR_ROLES,
  FONT_ROLES,
  BRAND_CRITICAL_ROLES,
  type FigmaMapResult,
  type MappedTokens,
  type ConfirmState,
  type ColorRole,
  type FontRole,
} from '../lib/figmaConfirm.ts';

/**
 * Figma Half B — the human-confirm step (figma-json-ingestion.md). After the mapper runs, the
 * designer confirms/overrides what it mapped (Mapped) and assigns or skips what it couldn't place
 * (Unmapped). Apply is BLOCKED until the brand-critical roles (background/copy/primary) are
 * resolved — the "never guess a brand-critical colour" guard. Apply → onConfirm(MappedTokens),
 * which the Brand editor merges into its tokens and Locks via the EXISTING path (no second write).
 * Co-equal with the artboard/AI path: the designer can Cancel and use that instead.
 */
const ROLE_LABEL: Record<ColorRole | FontRole, string> = {
  background: 'Background',
  copy: 'Copy (text)',
  copySubtle: 'Copy — subtle',
  primary: 'Primary',
  primaryHover: 'Primary — hover',
  heading: 'Heading font',
  body: 'Body font',
  accent: 'Accent font',
};

function Swatch({ value }: { value?: string }) {
  if (!value || !isColorValue(value) || value.trim().startsWith('#') === false) {
    return <span className="inline-block h-4 w-4 flex-none rounded border border-line bg-paper-2" aria-hidden />;
  }
  return <span className="inline-block h-4 w-4 flex-none rounded border border-line" style={{ background: value }} aria-hidden />;
}

export function FigmaConfirm({
  result,
  fileName,
  onConfirm,
  onCancel,
}: {
  result: FigmaMapResult;
  fileName: string;
  onConfirm: (mapped: MappedTokens) => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<ConfirmState>(() => seedConfirmState(result));
  const missing = useMemo(() => brandCriticalMissing(state), [state]);
  const [showNotes, setShowNotes] = useState(false);

  function setColor(role: ColorRole, v: string) {
    setState((s) => ({ ...s, color: { ...s.color, [role]: v } }));
  }
  function setFont(role: FontRole, v: string) {
    setState((s) => ({ ...s, fontFamilies: { ...s.fontFamilies, [role]: v } }));
  }
  // Assign an unmapped token's value into a role (colour → a colour role; font → a font role).
  function assign(kind: 'color' | 'font', role: string, value: string) {
    if (!value) return;
    if (kind === 'color') setColor(role as ColorRole, value);
    else setFont(role as FontRole, value);
  }

  const colorUnmapped = result.unmapped.filter((u) => u.kind === 'color');
  const otherUnmapped = result.unmapped.filter((u) => u.kind !== 'color');

  return (
    <div className="space-y-4 rounded border border-info bg-paper p-4 text-sm">
      <div>
        <p className="font-medium text-ink">Confirm the brand from <span className="font-mono">{fileName}</span></p>
        <p className={sectionDesc}>
          The Figma export mapped the values below. Review &amp; override them, assign anything it wasn’t sure
          about, then apply — it merges into the brand kit you Lock. This is one of two doors; you can also use
          the reference-image extraction.
        </p>
      </div>

      {/* Mapped — colours */}
      <div className="space-y-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-2">Colours</p>
        {COLOR_ROLES.map((role) => {
          const v = state.color[role] ?? '';
          const critical = BRAND_CRITICAL_ROLES.includes(role);
          const unset = critical && !isColorValue(v);
          return (
            <div key={role} className="flex items-center gap-2">
              <Swatch value={v} />
              <span className="w-32 flex-none text-ink">
                {ROLE_LABEL[role]}
                {critical && <span className="text-danger" title="brand-critical">*</span>}
              </span>
              <input
                value={v}
                onChange={(e) => setColor(role, e.target.value)}
                placeholder={critical ? 'required — #hex' : 'optional'}
                className={`w-40 rounded border px-2 py-0.5 font-mono text-xs outline-none ${
                  unset ? 'border-danger' : 'border-line'
                }`}
              />
            </div>
          );
        })}
      </div>

      {/* Mapped — fonts + type scale */}
      <div className="space-y-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-2">Fonts</p>
        {FONT_ROLES.map((role) => (
          <div key={role} className="flex items-center gap-2">
            <span className="w-32 flex-none text-ink">{ROLE_LABEL[role]}</span>
            <input
              value={state.fontFamilies[role] ?? ''}
              onChange={(e) => setFont(role, e.target.value)}
              placeholder="optional — e.g. 'Cormorant Garamond'"
              className="w-56 rounded border border-line px-2 py-0.5 text-xs outline-none"
            />
          </div>
        ))}
        {state.typeScale && state.typeScale.length > 0 && (
          <p className="text-[11px] text-ink-2">
            Type scale: <span className="font-mono">{state.typeScale.join(' · ')}</span> (applied as-is; fine-tune below after Lock)
          </p>
        )}
      </div>

      {/* Unmapped — assign to a role or skip */}
      {result.unmapped.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-2">
            Unmapped ({result.unmapped.length}) — assign or skip
          </p>
          <ul className="space-y-1">
            {colorUnmapped.map((u) => (
              <li key={u.name} className="flex flex-wrap items-center gap-2 rounded border border-line p-1.5">
                <Swatch value={u.value} />
                <span className="font-mono text-xs text-ink">{u.name}</span>
                {u.value && <span className="font-mono text-[11px] text-ink-2">{u.value}</span>}
                <span className="text-[11px] text-ink-2">— {u.reason}</span>
                <select
                  defaultValue=""
                  disabled={!u.value}
                  onChange={(e) => e.target.value && assign('color', e.target.value, u.value as string)}
                  className="ml-auto rounded border border-line px-1 py-0.5 text-[11px] disabled:opacity-40"
                  title={u.value ? 'Assign this value to a colour role' : 'No resolved value — set it in the Colours list above'}
                >
                  <option value="">{u.value ? 'assign to…' : 'no value'}</option>
                  {COLOR_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                  ))}
                </select>
              </li>
            ))}
            {otherUnmapped.map((u) => (
              <li key={u.name} className="flex flex-wrap items-center gap-2 rounded border border-line p-1.5">
                <span className="font-mono text-xs text-ink">{u.name}</span>
                <span className="rounded bg-paper-2 px-1 text-[10px] text-ink-2">{u.kind}</span>
                {u.value && <span className="font-mono text-[11px] text-ink-2">{u.value}</span>}
                <span className="text-[11px] text-ink-2">— {u.reason}</span>
                {u.kind === 'font' && u.value && (
                  <select
                    defaultValue=""
                    onChange={(e) => e.target.value && assign('font', e.target.value, u.value as string)}
                    className="ml-auto rounded border border-line px-1 py-0.5 text-[11px]"
                  >
                    <option value="">assign to…</option>
                    {FONT_ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                    ))}
                  </select>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.notes.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowNotes((v) => !v)} className="text-[11px] text-info underline">
            {showNotes ? 'Hide' : 'Show'} mapping notes ({result.notes.length})
          </button>
          {showNotes && (
            <ul className="mt-1 space-y-0.5 text-[11px] text-ink-2">
              {result.notes.map((n, i) => (
                <li key={i}>· {n}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {missing.length > 0 && (
        <p className="rounded border border-danger bg-danger-bright/10 p-2 text-xs text-danger">
          Assign a value for {missing.map((r) => ROLE_LABEL[r]).join(', ')} before applying — these are
          brand-critical and are never guessed.
        </p>
      )}

      <div className="flex items-center justify-end gap-3 border-t border-line pt-2">
        <button onClick={onCancel} className={btnGhost}>Cancel</button>
        <button onClick={() => onConfirm(toMappedTokens(state))} disabled={missing.length > 0} className={btnSolid}>
          Apply to the brand kit →
        </button>
      </div>
    </div>
  );
}
